// PL/SQL 객체 유형 → DBMS_METADATA.GET_DDL 의 유형 이름.
// **불러오기와 충돌 확인이 같은 표를 쓴다** — 둘이 다른 유형으로 GET_DDL 을 부르면 같은 객체라도
// 모양이 달라져(패키지는 명세만 vs 명세+본문) 컴파일할 때마다 「다른 곳에서 바뀌었다」 가 뜬다.
const PLSQL_META_TYPE = {
    PROCEDURE: 'PROCEDURE', FUNCTION: 'FUNCTION', PACKAGE: 'PACKAGE',
    'PACKAGE BODY': 'PACKAGE_BODY', TRIGGER: 'TRIGGER',
    TYPE: 'TYPE_SPEC', 'TYPE BODY': 'TYPE_BODY',
};

// PL/SQL 편집기의 객체·소유자 이름 검사 — **글자는 유니코드 전부**(Oracle 은 DB 문자셋이 지원하면 따옴표 없는 이름에
// 한글 등을 허용한다). 예전 `[A-Za-z0-9_$#가-힣]` 는 한글 음절만 받았다. 따옴표(')는 여전히 못 들어온다 — 문장에 그대로 끼운다.
// CREATE 해석(utils/plsqlCreate)의 맨 이름 모양과 같은 글자 집합이어야 한다.
export const PLSQL_NAME_RE = /^[\p{L}\p{N}\p{M}_$#]+$/u;

const dbQueries = {
    oracle: {
      fetchSchemas: "SELECT DISTINCT OWNER AS SCHEMA_NAME FROM ALL_TABLES WHERE OWNER NOT LIKE '%SYS%' AND OWNER NOT LIKE 'APEX%' AND OWNER NOT LIKE 'FLOWS%' ORDER BY OWNER",
      fetchAllTablesWithOwners: (schemaNames) => {
        if (!schemaNames.every(name => /^[A-Za-z0-9_$#가-힣]+$/.test(name))) {
          throw new Error('Invalid schema name detected.');
        }
        return `SELECT OWNER AS SCHEMA_NAME, TABLE_NAME FROM ALL_TABLES WHERE OWNER IN (${schemaNames.map(name => `'${name}'`).join(',')}) ORDER BY OWNER, TABLE_NAME`;
      },
      fetchAllViewsWithOwners: (schemaNames) => {
        if (!schemaNames.every(name => /^[A-Za-z0-9_$#가-힣]+$/.test(name))) {
          throw new Error('Invalid schema name detected.');
        }
        return `SELECT OWNER AS SCHEMA_NAME, VIEW_NAME FROM ALL_VIEWS WHERE OWNER IN (${schemaNames.map(name => `'${name}'`).join(',')}) ORDER BY OWNER, VIEW_NAME`;
      },
      fetchAllProceduresWithOwners: (schemaNames) => {
        if (!schemaNames.every(name => /^[A-Za-z0-9_$#가-힣]+$/.test(name))) {
          throw new Error('Invalid schema name detected.');
        }
        return `SELECT OWNER AS SCHEMA_NAME, OBJECT_NAME, OBJECT_TYPE FROM ALL_OBJECTS WHERE OWNER IN (${schemaNames.map(name => `'${name}'`).join(',')}) AND OBJECT_TYPE IN ('PROCEDURE','FUNCTION','PACKAGE') ORDER BY OWNER, OBJECT_TYPE, OBJECT_NAME`;
      },
      fetchColumnsForTable: (schemaName, tableName) => {
        if (!/^[A-Za-z0-9_$#가-힣]+$/.test(schemaName) || !/^[A-Za-z0-9_$#가-힣]+$/.test(tableName)) {
          throw new Error('Invalid schema or table name.');
        }
        return `SELECT 
  COLUMN_ID as columnid,  
  COLUMN_NAME as name, 
  DATA_TYPE as type, 
  CASE 
    WHEN DATA_TYPE = 'NUMBER' THEN 
      CASE 
        WHEN DATA_SCALE > 0 THEN TO_CHAR(DATA_PRECISION) || ',' || TO_CHAR(DATA_SCALE)
        ELSE TO_CHAR(DATA_PRECISION)
      END
    ELSE TO_CHAR(DATA_LENGTH)
  END as length, 
  NULLABLE as nullable, 
  (SELECT COMMENTS FROM ALL_COL_COMMENTS A WHERE A.OWNER = '${schemaName}' AND A.TABLE_NAME = '${tableName}' AND A.COLUMN_NAME = UTC.COLUMN_NAME) as comments 
FROM ALL_TAB_COLUMNS UTC 
WHERE OWNER = '${schemaName}' AND TABLE_NAME = '${tableName}' 
ORDER BY COLUMN_ID`;
      },
      fetchIndexesForTable: (schemaName, tableName) => {
        if (!/^[A-Za-z0-9_$#가-힣]+$/.test(schemaName) || !/^[A-Za-z0-9_$#가-힣]+$/.test(tableName)) {
          throw new Error('Invalid schema or table name.');
        }
        // 인덱스 **소유자**까지 묶는다. 예전에는 조인이 `INDEX_OWNER`+`INDEX_NAME` 뿐이고
        // GROUP BY 에 `UI.OWNER` 가 없어서, 다른 계정이 만든 동명 인덱스가 있으면 같은 이름이
        // 두 줄로 나오거나(UNIQUENESS 가 다를 때) 컬럼이 뒤섞여 한 줄로 합쳐졌다(신고 2026-09-18).
        return `SELECT
	UI.OWNER AS indexOwner,
	UI.INDEX_NAME AS name,
	LISTAGG(UIC.COLUMN_NAME, ', ') WITHIN GROUP (
	ORDER BY UIC.COLUMN_POSITION) AS columnsString,
	DECODE(UI.UNIQUENESS, 'UNIQUE', 'Unique', 'Non-Unique') AS ISUNIQUE
FROM
	ALL_INDEXES UI
JOIN ALL_IND_COLUMNS UIC ON
	UI.OWNER = UIC.INDEX_OWNER
	AND UI.INDEX_NAME = UIC.INDEX_NAME
	AND UI.TABLE_OWNER = UIC.TABLE_OWNER
	AND UI.TABLE_NAME = UIC.TABLE_NAME
WHERE UI.TABLE_OWNER = '${schemaName}' 
  AND UI.TABLE_NAME = '${tableName}'
GROUP BY
	UI.OWNER,
	UI.INDEX_NAME,
	UI.UNIQUENESS
ORDER BY
	UI.INDEX_NAME`;
      },
      getExecutionPlanQuery: (query, statementId) => {
        if (!/^[A-Za-z0-9_$#가-힣]+$/.test(statementId)) {
          throw new Error('Invalid statement ID.');
        }
        return `EXPLAIN PLAN SET STATEMENT_ID = '${statementId}' FOR ${query}`;
      },
      fetchDisplayPlanQuery: (statementId) => {
        if (!/^[A-Za-z0-9_$#가-힣]+$/.test(statementId)) {
          throw new Error('Invalid statement ID.');
        }
        return `SELECT PLAN_TABLE_OUTPUT FROM TABLE(DBMS_XPLAN.DISPLAY('PLAN_TABLE', '${statementId}', 'ALL'))`;
      },
      fetchViewDDL: (schemaName, viewName) => {
        if (!/^[A-Za-z0-9_$#가-힣]+$/.test(schemaName) || !/^[A-Za-z0-9_$#가-힣]+$/.test(viewName)) {
          throw new Error('Invalid schema or view name.');
        }
        return `SELECT TEXT AS DDL_TEXT FROM ALL_VIEWS WHERE OWNER = '${schemaName}' AND VIEW_NAME = '${viewName}'`;
      },
      fetchProcedureDDL: (schemaName, objectName) => {
        if (!/^[A-Za-z0-9_$#가-힣]+$/.test(schemaName) || !/^[A-Za-z0-9_$#가-힣]+$/.test(objectName)) {
          throw new Error('Invalid schema or object name.');
        }
        return `SELECT TEXT AS DDL_TEXT FROM ALL_SOURCE WHERE OWNER = '${schemaName}' AND NAME = '${objectName}' ORDER BY LINE`;
      },
      // ===== PL/SQL 편집기(편집/컴파일/실행) =====
      // 현재 연결 스키마(USER_* 문맥과 동일한 OWNER)에 국한 — ALTER ... COMPILE 권한과도 일치.
      fetchPlsqlObjects: (schemaName) => {
        if (!PLSQL_NAME_RE.test(schemaName)) {
          throw new Error('Invalid schema name.');
        }
        return `SELECT OBJECT_NAME, OBJECT_TYPE, STATUS, LAST_DDL_TIME
                FROM ALL_OBJECTS
                WHERE OWNER = '${schemaName}'
                  AND OBJECT_TYPE IN ('PROCEDURE','FUNCTION','PACKAGE','PACKAGE BODY','TRIGGER')
                ORDER BY OBJECT_TYPE, OBJECT_NAME`;
      },
      // DBMS_METADATA.GET_DDL — SQL*Plus 의 USER_SOURCE 라인 조립과 달리 완결된
      // "CREATE OR REPLACE ... END;" 한 문장을 그대로 반환해 편집 후 재실행(컴파일)이 가능하다.
      fetchPlsqlSource: (schemaName, objectType, objectName) => {
        if (!PLSQL_NAME_RE.test(schemaName) || !PLSQL_NAME_RE.test(objectName)) {
          throw new Error('Invalid schema or object name.');
        }
        const metaType = PLSQL_META_TYPE[objectType];
        if (!metaType) throw new Error('Unsupported PL/SQL object type.');
        return `SELECT DBMS_METADATA.GET_DDL('${metaType}', '${objectName}', '${schemaName}') AS DDL_TEXT FROM DUAL`;
      },
      // 컴파일 **직전** DB 의 현재 소스 — 덮어쓰기 충돌 확인과 「덮어쓰기 전 원본」 보관에 쓴다.
      // 객체가 없으면 0행(GET_DDL 을 바로 부르면 ORA-31603 으로 죽는다). OWNER 도 돌려준다 —
      // 소유자를 안 적은 CREATE 는 세션의 현재 스키마에 들어가므로 그것을 알아야 기준선과 맞출 수 있다.
      fetchPlsqlCurrentSource: (owner, objectType, objectName) => {
        const ok = PLSQL_NAME_RE;
        if ((owner != null && !ok.test(owner)) || !ok.test(objectName)) {
          throw new Error('Invalid schema or object name.');
        }
        const metaType = PLSQL_META_TYPE[objectType];
        if (!metaType) throw new Error('Unsupported PL/SQL object type.');
        const ownerExpr = owner != null ? `'${owner}'` : `SYS_CONTEXT('USERENV', 'CURRENT_SCHEMA')`;
        return `SELECT o.OWNER, DBMS_METADATA.GET_DDL('${metaType}', o.OBJECT_NAME, o.OWNER) AS DDL_TEXT
                FROM ALL_OBJECTS o
                WHERE o.OWNER = ${ownerExpr} AND o.OBJECT_NAME = '${objectName}' AND o.OBJECT_TYPE = '${objectType}'`;
      },
      /**
       * 테이블 **전체 DDL** — 테이블 본문 + 딸린 인덱스.
       *
       * `GET_DDL('TABLE')` 만으로는 인덱스가 빠진다. 그렇다고 `GET_DEPENDENT_DDL` 을 쓰면
       * 딸린 것이 하나도 없을 때 ORA-31608 로 **쿼리 전체가 실패**한다 — 인덱스 없는 테이블이
       * 드물지 않다. 그래서 ALL_INDEXES 를 돌며 개별 GET_DDL 을 UNION ALL 한다(없으면 0행).
       *
       * PK·UNIQUE 를 받치는 인덱스는 제외한다 — 테이블 DDL 의 제약조건에 이미 들어 있어
       * 그대로 실행하면 같은 인덱스를 두 번 만들려다 실패한다.
       */
      fetchTableDDL: (schemaName, tableName) => {
        if (!/^[A-Za-z0-9_$#가-힣]+$/.test(schemaName) || !/^[A-Za-z0-9_$#가-힣]+$/.test(tableName)) {
          throw new Error('Invalid schema or table name.');
        }
        return `SELECT DBMS_METADATA.GET_DDL('TABLE', '${tableName}', '${schemaName}') AS DDL_TEXT FROM DUAL
                UNION ALL
                SELECT DBMS_METADATA.GET_DDL('INDEX', i.INDEX_NAME, i.OWNER)
                  FROM ALL_INDEXES i
                 WHERE i.TABLE_OWNER = '${schemaName}'
                   AND i.TABLE_NAME = '${tableName}'
                   AND NOT EXISTS (
                       SELECT 1 FROM ALL_CONSTRAINTS c
                        WHERE c.OWNER = i.OWNER
                          AND c.INDEX_NAME = i.INDEX_NAME
                          AND c.CONSTRAINT_TYPE IN ('P', 'U'))`;
      },
      /**
       * 카탈로그로 조립한 테이블 DDL — **DBMS_METADATA 를 못 쓰는 계정용.**
       *
       * `GET_DDL` 은 **다른 스키마** 객체에 `SELECT_CATALOG_ROLE` 을 요구한다. 없으면
       * ORA-31603 으로 실패한다 — 교육 DB 의 EDU 계정으로 HR 테이블을 볼 때 실제로 그랬다.
       * 조회는 되는데 DDL 만 못 보는 셈이라, ALL_* 만으로 같은 것을 조립한다.
       *
       * **완전한 재현은 아니다**: 기본값(DEFAULT)·저장 옵션·파티션 절·LOB 절은 빠진다.
       * DEFAULT 는 ALL_TAB_COLUMNS 에서 LONG 이라 SQL 로 이어붙일 수 없다.
       * 그래서 GET_DDL 이 되는 계정에서는 그쪽을 먼저 쓴다(호출부가 차례로 시도).
       */
      fetchTableDDLFromCatalog: (schemaName, tableName) => {
        if (!/^[A-Za-z0-9_$#가-힣]+$/.test(schemaName) || !/^[A-Za-z0-9_$#가-힣]+$/.test(tableName)) {
          throw new Error('Invalid schema or table name.');
        }
        const o = schemaName; const tb = tableName;
        // **컬럼마다 한 줄씩 낸다**(2026-10-13). 예전에는 CREATE TABLE 전체를 LISTAGG 한 문자열로 만들어, 컬럼이 100개쯤 되면
        // 4000바이트를 넘어 ORA-01489(result of string concatenation is too long)로 죽었다. 줄은 부르는 쪽이 이어 붙인다
        const hasCols = `EXISTS (SELECT 1 FROM ALL_TAB_COLUMNS x WHERE x.OWNER = '${o}' AND x.TABLE_NAME = '${tb}')`;
        return `SELECT DDL_TEXT FROM (
  SELECT 1 AS ORD, 0 AS SEQ, 'CREATE TABLE "${o}"."${tb}" (' AS DDL_TEXT FROM DUAL WHERE ${hasCols}
  UNION ALL
  SELECT 1, ROW_NUMBER() OVER (ORDER BY c.COLUMN_ID),
         '    "' || c.COLUMN_NAME || '" ' ||
             CASE
               WHEN c.DATA_TYPE IN ('VARCHAR2','NVARCHAR2','CHAR','NCHAR','RAW')
                 THEN c.DATA_TYPE || '(' || c.CHAR_LENGTH || CASE WHEN c.CHAR_USED = 'C' THEN ' CHAR' ELSE '' END || ')'
               WHEN c.DATA_TYPE = 'NUMBER' AND c.DATA_PRECISION IS NOT NULL
                 THEN 'NUMBER(' || c.DATA_PRECISION || CASE WHEN NVL(c.DATA_SCALE,0) > 0 THEN ',' || c.DATA_SCALE ELSE '' END || ')'
               ELSE c.DATA_TYPE
             END ||
             CASE WHEN c.NULLABLE = 'N' THEN ' NOT NULL' ELSE '' END ||
             CASE WHEN ROW_NUMBER() OVER (ORDER BY c.COLUMN_ID) < COUNT(*) OVER () THEN ',' ELSE '' END
    FROM ALL_TAB_COLUMNS c
   WHERE c.OWNER = '${o}' AND c.TABLE_NAME = '${tb}'
  UNION ALL
  SELECT 1, 999999, ');' FROM DUAL WHERE ${hasCols}
  UNION ALL
  SELECT 2, 0, 'ALTER TABLE "${o}"."${tb}" ADD CONSTRAINT "' || con.CONSTRAINT_NAME || '" ' ||
         CASE con.CONSTRAINT_TYPE WHEN 'P' THEN 'PRIMARY KEY' WHEN 'U' THEN 'UNIQUE' ELSE 'FOREIGN KEY' END || ' (' ||
         (SELECT LISTAGG('"' || cc.COLUMN_NAME || '"', ', ') WITHIN GROUP (ORDER BY cc.POSITION)
            FROM ALL_CONS_COLUMNS cc
           WHERE cc.OWNER = con.OWNER AND cc.CONSTRAINT_NAME = con.CONSTRAINT_NAME) || ')' ||
         CASE WHEN con.CONSTRAINT_TYPE = 'R' THEN
           ' REFERENCES "' || rcon.OWNER || '"."' || rcon.TABLE_NAME || '" (' ||
           (SELECT LISTAGG('"' || rc.COLUMN_NAME || '"', ', ') WITHIN GROUP (ORDER BY rc.POSITION)
              FROM ALL_CONS_COLUMNS rc
             WHERE rc.OWNER = rcon.OWNER AND rc.CONSTRAINT_NAME = rcon.CONSTRAINT_NAME) || ')'
         ELSE '' END || ';'
    FROM ALL_CONSTRAINTS con
    LEFT JOIN ALL_CONSTRAINTS rcon
      ON rcon.OWNER = con.R_OWNER AND rcon.CONSTRAINT_NAME = con.R_CONSTRAINT_NAME
   WHERE con.OWNER = '${o}' AND con.TABLE_NAME = '${tb}'
     AND con.CONSTRAINT_TYPE IN ('P','U','R')
  UNION ALL
  SELECT 3, 0, 'CREATE ' || CASE WHEN i.UNIQUENESS = 'UNIQUE' THEN 'UNIQUE ' ELSE '' END ||
         'INDEX "' || i.OWNER || '"."' || i.INDEX_NAME || '" ON "' || i.TABLE_OWNER || '"."' || i.TABLE_NAME || '" (' ||
         (SELECT LISTAGG('"' || ic.COLUMN_NAME || '"', ', ') WITHIN GROUP (ORDER BY ic.COLUMN_POSITION)
            FROM ALL_IND_COLUMNS ic
           WHERE ic.INDEX_OWNER = i.OWNER AND ic.INDEX_NAME = i.INDEX_NAME) || ');'
    FROM ALL_INDEXES i
   WHERE i.TABLE_OWNER = '${o}' AND i.TABLE_NAME = '${tb}'
     AND NOT EXISTS (SELECT 1 FROM ALL_CONSTRAINTS c2
                      WHERE c2.OWNER = i.OWNER AND c2.INDEX_NAME = i.INDEX_NAME
                        AND c2.CONSTRAINT_TYPE IN ('P','U'))
) ORDER BY ORD, SEQ`;
      },
      // 컴파일 에러 — CREATE OR REPLACE 실행 직후 또는 ALTER ... COMPILE 직후 조회.
      fetchPlsqlErrors: (schemaName, objectType, objectName) => {
        if (!PLSQL_NAME_RE.test(schemaName) || !PLSQL_NAME_RE.test(objectName)) {
          throw new Error('Invalid schema or object name.');
        }
        return `SELECT LINE, POSITION, TEXT, ATTRIBUTE
                FROM ALL_ERRORS
                WHERE OWNER = '${schemaName}' AND NAME = '${objectName}' AND TYPE = '${objectType}'
                ORDER BY SEQUENCE`;
      },
      // 컴파일 **결과** — 객체 상태와 오류를 한 번에. PL/SQL 편집기의 성공 판정은 이것으로만 한다.
      //
      // 오류 0건만 보고 「성공」이라 하면 안 된다: 객체를 못 찾아도(이름·소유자가 어긋나도) 0건이다.
      // 그래서 ALL_OBJECTS 에서 **객체를 찾았고 VALID 일 때만** 성공이다(0행 = 확인 못 함).
      // owner 가 null 이면 세션의 현재 스키마 — 소유자 없이 쓴 CREATE 는 거기 만들어지고, 컴파일과
      // 이 조회가 같은 창 세션에서 돌므로 정확히 그 스키마다(화면의 스키마 칸과 다를 수 있다).
      fetchPlsqlCompileResult: (owner, objectType, objectName) => {
        const ok = PLSQL_NAME_RE;
        if ((owner != null && !ok.test(owner)) || !ok.test(objectName) || !/^[A-Z ]+$/.test(objectType)) {
          throw new Error('Invalid schema or object name.');
        }
        const ownerExpr = owner != null ? `'${owner}'` : `SYS_CONTEXT('USERENV', 'CURRENT_SCHEMA')`;
        return `SELECT o.STATUS, e.LINE, e.POSITION, e.TEXT, e.ATTRIBUTE
                FROM ALL_OBJECTS o
                LEFT JOIN ALL_ERRORS e
                  ON e.OWNER = o.OWNER AND e.NAME = o.OBJECT_NAME AND e.TYPE = o.OBJECT_TYPE
                WHERE o.OWNER = ${ownerExpr} AND o.OBJECT_NAME = '${objectName}' AND o.OBJECT_TYPE = '${objectType}'
                ORDER BY e.SEQUENCE`;
      },
      // 프로시저/함수 파라미터 — "테스트 실행" 호출 스켈레톤 자동 생성용.
      fetchPlsqlArguments: (schemaName, objectName) => {
        if (!PLSQL_NAME_RE.test(schemaName) || !PLSQL_NAME_RE.test(objectName)) {
          throw new Error('Invalid schema or object name.');
        }
        return `SELECT ARGUMENT_NAME, DATA_TYPE, IN_OUT, POSITION, OVERLOAD
                FROM ALL_ARGUMENTS
                WHERE OWNER = '${schemaName}' AND OBJECT_NAME = '${objectName}' AND ARGUMENT_NAME IS NOT NULL
                ORDER BY OVERLOAD, POSITION`;
      },
      // 소스 변경 없이 강제 재컴파일 (의존 오브젝트 변경으로 INVALID 된 경우 등).
      buildPlsqlCompileStatement: (objectType, schemaName, objectName) => {
        if (!PLSQL_NAME_RE.test(schemaName) || !PLSQL_NAME_RE.test(objectName)) {
          throw new Error('Invalid schema or object name.');
        }
        const qualified = `"${schemaName}"."${objectName}"`;
        switch (objectType) {
          case 'PROCEDURE': return `ALTER PROCEDURE ${qualified} COMPILE`;
          case 'FUNCTION': return `ALTER FUNCTION ${qualified} COMPILE`;
          case 'TRIGGER': return `ALTER TRIGGER ${qualified} COMPILE`;
          case 'PACKAGE': return `ALTER PACKAGE ${qualified} COMPILE`;
          case 'PACKAGE BODY': return `ALTER PACKAGE ${qualified} COMPILE BODY`;
          default: throw new Error('Unsupported PL/SQL object type.');
        }
      },
      // 의존성 트리 — 이 오브젝트가 참조하는 것들 (ALL_DEPENDENCIES.TYPE 은 'PACKAGE BODY'도 공백형 그대로 사용).
      fetchPlsqlDependsOn: (schemaName, objectType, objectName) => {
        if (!PLSQL_NAME_RE.test(schemaName) || !PLSQL_NAME_RE.test(objectName)) {
          throw new Error('Invalid schema or object name.');
        }
        return `SELECT REFERENCED_OWNER, REFERENCED_NAME, REFERENCED_TYPE
                FROM ALL_DEPENDENCIES
                WHERE OWNER = '${schemaName}' AND NAME = '${objectName}' AND TYPE = '${objectType}'
                ORDER BY REFERENCED_TYPE, REFERENCED_NAME`;
      },
      // 이 오브젝트를 참조하는 것들 (역방향)
      fetchPlsqlDependents: (schemaName, objectType, objectName) => {
        if (!PLSQL_NAME_RE.test(schemaName) || !PLSQL_NAME_RE.test(objectName)) {
          throw new Error('Invalid schema or object name.');
        }
        return `SELECT OWNER, NAME, TYPE
                FROM ALL_DEPENDENCIES
                WHERE REFERENCED_OWNER = '${schemaName}' AND REFERENCED_NAME = '${objectName}' AND REFERENCED_TYPE = '${objectType}'
                ORDER BY TYPE, NAME`;
      }
    },
    // dbType 키는 backend manifest 의 dbType 값과 정확히 일치해야 함 ('postgresql', not 'postgres').
    // 어댑터 등록·연결·UI 분기 모두 'postgresql' 사용 중.
    postgresql: {
      fetchSchemas: "SELECT nspname AS SCHEMA_NAME FROM pg_catalog.pg_namespace WHERE nspname NOT LIKE 'pg_%' AND nspname != 'information_schema' ORDER BY nspname",
      fetchAllTablesWithOwners: (schemaNames) => {
        if (!schemaNames.every(name => /^[A-Za-z0-9_$#가-힣]+$/.test(name))) {
          throw new Error('Invalid schema name detected.');
        }
        return `SELECT schemaname AS SCHEMA_NAME, tablename AS TABLE_NAME FROM pg_tables WHERE schemaname NOT LIKE 'pg_%' AND schemaname != 'information_schema' AND schemaname IN (${schemaNames.map(name => `'${name}'`).join(',')}) ORDER BY schemaname, tablename`;
      },
      fetchColumnsForTable: (schemaName, tableName) => {
        if (!/^[A-Za-z0-9_$#가-힣]+$/.test(schemaName) || !/^[A-Za-z0-9_$#가-힣]+$/.test(tableName)) {
          throw new Error('Invalid schema or table name.');
        }
        return `SELECT 
                  a.attname as name, 
                  format_type(a.atttypid, a.atttypmod) as type, 
                  CASE WHEN a.atttypmod > 0 THEN substring(format_type(a.atttypid, a.atttypmod) from '\\((.*)\\)') ELSE NULL END as length, 
                  CASE WHEN a.attnotnull THEN 'N' ELSE 'Y' END as nullable, 
                  d.description as comment 
                FROM pg_catalog.pg_attribute a 
                JOIN pg_catalog.pg_class c ON a.attrelid = c.oid 
                JOIN pg_catalog.pg_namespace n ON c.relnamespace = n.oid 
                LEFT JOIN pg_catalog.pg_description d ON d.objoid = a.attrelid AND d.objsubid = a.attnum 
                WHERE n.nspname = '${schemaName}' AND c.relname = '${tableName}' AND a.attnum > 0 AND NOT a.attisdropped 
                ORDER BY a.attnum`;
      },
      fetchIndexesForTable: (schemaName, tableName) => {
        if (!/^[A-Za-z0-9_$#가-힣]+$/.test(schemaName) || !/^[A-Za-z0-9_$#가-힣]+$/.test(tableName)) {
          throw new Error('Invalid schema or table name.');
        }
        return `SELECT 
                  i.relname as name, 
                  array_to_string(array_agg(a.attname ORDER BY ix.indkey::text ~ ('\\'' || a.attnum || '\\'')), ', ') as columnsString, 
                  ix.indisunique as isUniqueBoolean 
                FROM pg_catalog.pg_class t 
                JOIN pg_catalog.pg_namespace n ON n.oid = t.relnamespace 
                JOIN pg_catalog.pg_index ix ON t.oid = ix.indrelid 
                JOIN pg_catalog.pg_class i ON i.oid = ix.indexrelid 
                JOIN pg_catalog.pg_attribute a ON a.attrelid = t.oid AND a.attnum = ANY(ix.indkey)
                WHERE n.nspname = '${schemaName}' AND t.relname = '${tableName}'
                GROUP BY i.relname, ix.indisunique
                ORDER BY i.relname`;
      },
      // 뷰 + materialized view 모두 (UNION). pg_views 는 일반 view, pg_matviews 는 mat view.
      fetchAllViewsWithOwners: (schemaNames) => {
        if (!schemaNames.every(name => /^[A-Za-z0-9_$#가-힣]+$/.test(name))) {
          throw new Error('Invalid schema name detected.');
        }
        const list = schemaNames.map(name => `'${name}'`).join(',');
        return `SELECT schemaname AS SCHEMA_NAME, viewname AS VIEW_NAME FROM pg_catalog.pg_views WHERE schemaname IN (${list})
                UNION ALL
                SELECT schemaname AS SCHEMA_NAME, matviewname AS VIEW_NAME FROM pg_catalog.pg_matviews WHERE schemaname IN (${list})
                ORDER BY 1, 2`;
      },
      // function/procedure/aggregate/window. PG 11+ 의 prokind 사용 (10 이하는 proisagg/proiswindow flag).
      // OID 도 함께 반환 — 같은 이름의 overloaded 함수 (예: fn_term_decomp2) 도 unique 하게 식별.
      fetchAllProceduresWithOwners: (schemaNames) => {
        if (!schemaNames.every(name => /^[A-Za-z0-9_$#가-힣]+$/.test(name))) {
          throw new Error('Invalid schema name detected.');
        }
        const list = schemaNames.map(name => `'${name}'`).join(',');
        return `SELECT n.nspname AS SCHEMA_NAME, p.proname AS OBJECT_NAME, p.oid AS OBJECT_OID,
                       pg_get_function_identity_arguments(p.oid) AS OBJECT_ARGS,
                       CASE p.prokind WHEN 'f' THEN 'FUNCTION' WHEN 'p' THEN 'PROCEDURE'
                            WHEN 'a' THEN 'AGGREGATE' WHEN 'w' THEN 'WINDOW' ELSE 'FUNCTION' END AS OBJECT_TYPE
                FROM pg_catalog.pg_proc p
                JOIN pg_catalog.pg_namespace n ON n.oid = p.pronamespace
                WHERE n.nspname IN (${list})
                ORDER BY n.nspname, p.proname, p.oid`;
      },
      // 뷰 정의문 (CREATE VIEW 본문) 조회.
      fetchViewDDL: (schemaName, viewName) => {
        if (!/^[A-Za-z0-9_$#가-힣]+$/.test(schemaName) || !/^[A-Za-z0-9_$#가-힣]+$/.test(viewName)) {
          throw new Error('Invalid schema or view name.');
        }
        return `SELECT pg_get_viewdef('${schemaName}.${viewName}'::regclass, true) AS DDL_TEXT`;
      },
      // 함수/프로시저 본문 — pg_get_functiondef 는 FULL CREATE 문 반환.
      fetchProcedureDDL: (schemaName, procName) => {
        if (!/^[A-Za-z0-9_$#가-힣]+$/.test(schemaName) || !/^[A-Za-z0-9_$#가-힣]+$/.test(procName)) {
          throw new Error('Invalid schema or procedure name.');
        }
        return `SELECT pg_get_functiondef(p.oid) AS DDL_TEXT
                FROM pg_catalog.pg_proc p
                JOIN pg_catalog.pg_namespace n ON n.oid = p.pronamespace
                WHERE n.nspname = '${schemaName}' AND p.proname = '${procName}'
                LIMIT 1`;
      },
      getExecutionPlanQuery: (query) => `EXPLAIN ${query}`
    },

    // SQLite: 내장 monitoring.db 조회용 (최소 셋)
    // - 스키마 개념이 없으므로 항상 단일 'main' 스키마 반환
    // - PRAGMA 기반 메타데이터 조회
    sqlite: {
      fetchSchemas: "SELECT 'main' AS SCHEMA_NAME",
      fetchAllTablesWithOwners: (schemaNames) => {
        // schemaNames 무시 (SQLite는 main만)
        return "SELECT 'main' AS SCHEMA_NAME, name AS TABLE_NAME FROM sqlite_master WHERE type='table' AND name NOT LIKE 'sqlite_%' ORDER BY name";
      },
      fetchAllViewsWithOwners: (schemaNames) => {
        return "SELECT 'main' AS SCHEMA_NAME, name AS VIEW_NAME FROM sqlite_master WHERE type='view' ORDER BY name";
      },
      fetchAllProceduresWithOwners: (schemaNames) => {
        // SQLite는 프로시저/함수 개념이 없음 → 빈 결과
        return "SELECT 'main' AS SCHEMA_NAME, '' AS OBJECT_NAME, '' AS OBJECT_TYPE WHERE 1=0";
      },
      fetchColumnsForTable: (schemaName, tableName) => {
        if (!/^[A-Za-z0-9_$#가-힣]+$/.test(tableName)) {
          throw new Error('Invalid table name.');
        }
        // Oracle은 unquoted alias를 자동 대문자화, SQLite는 대소문자 보존
        // → 대문자 별칭으로 Oracle 응답 형식(COLUMNID, NAME, ...)과 맞춤
        return `SELECT
                  cid AS COLUMNID,
                  name AS NAME,
                  type AS TYPE,
                  NULL AS LENGTH,
                  CASE WHEN "notnull"=1 THEN 'N' ELSE 'Y' END AS NULLABLE,
                  dflt_value AS DEFAULTVALUE,
                  pk AS PKORDER,
                  NULL AS COMMENTS
                FROM pragma_table_info('${tableName}')
                ORDER BY cid`;
      },
      fetchIndexesForTable: (schemaName, tableName) => {
        if (!/^[A-Za-z0-9_$#가-힣]+$/.test(tableName)) {
          throw new Error('Invalid table name.');
        }
        return `SELECT
                  il.name AS NAME,
                  GROUP_CONCAT(ii.name, ', ') AS COLUMNSSTRING,
                  CASE WHEN il."unique"=1 THEN 1 ELSE 0 END AS ISUNIQUEBOOLEAN
                FROM pragma_index_list('${tableName}') il
                LEFT JOIN pragma_index_info(il.name) ii
                GROUP BY il.name, il."unique"
                ORDER BY il.name`;
      },
      getExecutionPlanQuery: (query) => `EXPLAIN QUERY PLAN ${query}`
    }
  };

  export default dbQueries;